import BaseModel from './BaseModel';
import Change from './Change';
import Client from './Client';
import Project from './Project';
import User from './User';
import moment from 'moment';
import type { Moment } from 'moment';
import _ from 'lodash';

/** Class representing a time track entry. */
class Track extends BaseModel {
    /**
     * Track tags constants
     */
    static TAG_FREE    = 'free';
    static TAG_DEV     = 'dev';
    static TAG_PACKAGE = 'package';
    static TAG_FLOOR   = 'floor';
    static TAG_247     = '24/7';

    static BILLING_MODES = ['hourly', 'package', 'free', 'floor'] as const;

    _id: string;
    author?: User;
    project: Project;
    client?: Client;
    change?: Change;
    title: string;
    /** Optional intervention notes (plain text). */
    description?: string;
    tags?: string[];
    /**
     * Whether this track is billable.
     * Derived from the absence of the `free` tag.
     */
    billable?: boolean;
    /** hourly | package | free | floor — mirrors zone-client import tags. */
    billingMode?: 'hourly' | 'package' | 'free' | 'floor';
    /** `dev` tag (coeff 1.3 on import). */
    billingDev?: boolean;
    /** Optional kilometers (mileage plugin). */
    km?: number;
    duration?: {
        iso: string;
        seconds: number;
        hours: number;
        minutes: number;
    };
    startAt?: Moment;
    stopAt?: Moment;
    createdAt?: Moment;
    updatedAt?: Moment;

    constructor(properties: AnyObj) {
        super({});

        this._id      = properties._id;
        this.title    = properties.title;
        this.description = properties.description || '';
        const tags = properties.tags || [];
        this.tags     = tags;
        this.billable = properties.billable !== undefined
            ? !!properties.billable
            : !tags.includes(Track.TAG_FREE);
        this.billingMode = properties.billingMode
            || Track.billingModeFromTags(tags);
        this.billingDev = properties.billingDev !== undefined
            ? !!properties.billingDev
            : tags.includes(Track.TAG_DEV);
        if (properties.km != null && properties.km !== '')
            this.km = Number(properties.km);
        this.duration = properties.duration || {
            iso: 'PT0',
            seconds: 0,
            minutes: 0,
            hours: 0
        };
        this.project  = properties.project;
        if (_.isObject(this.project))
            this.project = new Project(this.project);

        this.author = properties.author;
        if (_.isObject(this.author))
            this.author = new User(this.author);

        this.client = properties.client;
        if (_.isObject(this.client))
            this.client = new Client(this.client);

        this.change = properties.change;
        if (_.isObject(this.change))
            this.change = new Change(this.change);

        // Do not invent startAt on create: moment(undefined) uses the browser
        // clock and can be rejected server-side as "in the future".
        if (properties.startAt != null && properties.startAt !== '')
            this.startAt = moment(properties.startAt);
        if (properties.stopAt)
            this.stopAt = moment(properties.stopAt);
        if (properties.createdAt)
            this.createdAt = moment(properties.createdAt);
        if (properties.updatedAt)
            this.updatedAt = moment(properties.updatedAt);
    }

    /**
     * Derive billing mode from track tags (zone-client import contract).
     */
    static billingModeFromTags(tags: string[] = []): 'hourly' | 'package' | 'free' | 'floor' {
        if (tags.includes(Track.TAG_FREE))
            return 'free';
        if (tags.includes(Track.TAG_PACKAGE))
            return 'package';
        if (tags.includes(Track.TAG_FLOOR))
            return 'floor';
        return 'hourly';
    }

    /**
     * Check if this track is running
     *
     * @return {Boolean}
     */
    isRunning(): boolean {
        return !this.stopAt;
    }

    /**
     * Get this track duration in seconds
     *
     * @return {Number}
     */
    seconds(): number {
        if (this.duration?.seconds && (this.duration.seconds > 0))
            return Math.round(this.duration.seconds);

        return (this.stopAt || moment()).diff(this.startAt || moment(), 'seconds');
    }

    /**
     * Get a formatted duration
     *
     * @return {String}
     */
    formattedDuration(): string {
        const duration = this.seconds();
        if (!duration)
            return '00:00';

        return Track.format(duration);
    }

    /**
     * Get a formatted string of a number duration
     *
     * @param {Number} duration in seconds
     *
     * @return {String}
     */
    static format(duration: number): string {
        const hours   = Math.floor(duration / 3600);
        const minutes = Math.floor((duration % 3600) / 60);
        const seconds = Math.floor(duration % 60);

        if (hours > 0)
            return [
                `${hours < 10 ? '0' : ''}${hours}`,
                `${minutes < 10 ? '0' : ''}${minutes}`,
                `${seconds < 10 ? '0' : ''}${seconds}`
            ].join(':')

        return [
            `${minutes < 10 ? '0' : ''}${minutes}`,
            `${seconds < 10 ? '0' : ''}${seconds}`
        ].join(':')
    }
}

export default Track;
